import RenderManager from "../rendering/render-manager";
import Network from "../miniviews/network";
import AssetLibrary from "../assets/asset-library";
import Event from "../utils/event";

import subclip from './subclip';

import { loadAnimation } from "../assets/deserialize-animations";
import AssetDeserializer from "../assets/asset-deserializer";
import SlideManager from "../utils/slide-manager";
import * as presets from "./presets/presets";
import generateAnimatedBoundingBox from "../utils/generate-animated-bbox";
import SystemDefaults from "../system-defaults";

const Status = { NOT_PLAYING: 1, PLAYING: 2, PAUSED: 3 };

class Q3AnimationMixer extends THREE.AnimationMixer {
    constructor() {
        super(RenderManager.root);

        this.Status = Status;
        this.paused = false;
        this.animationDelayer = new Map();

        //override original method so that it could bing animation based on original node name (in case it was changed by a user)
        THREE.PropertyBinding.findNode = findNode;

        this.objectsActions = {};
        this._waitingObjectAnimation = {};
        this._partialAnimClips = {};
        this._actionStopTimers = new WeakMap();

        this.addEventListener('finished', this._actionFinished.bind(this));

        this.actionStatusChanged = new Event();
    }

    pause() {
        Object
            .keys(Q3.mixer.objectsActions)
            .forEach(uuid => {
                const objAct = Q3.mixer.objectsActions[uuid];

                if (!objAct.activeAction) return;

                this.stopObjectCurrentAnimation(uuid, -1);
            });
    }

    resume() {
        Object
            .keys(Q3.mixer.objectsActions)
            .forEach(uuid => {
                const objAct = Q3.mixer.objectsActions[uuid];

                if (objAct.activeAction) return;

                const slideAnim = SlideManager.currentSlide.animation[uuid];

                if (!slideAnim) return;

                const animation = slideAnim.animation;

                if (!animation || animation.type === THREE.LoopOnce) return;

                if (animation.isPreset) {
                    this.createAndPlayPresetAnimation(Object.assign({
                        objectUuid: uuid,
                        loopType: animation.type,
                        timescale: animation.timescale,
                        fade: -1
                    }, animation.preset));
                } else {
                    this.playObjectAnimation({
                        objectUuid: uuid,
                        animationUuid: animation.uuid,
                        loopType: animation.type,
                        timescale: animation.timescale,
                        fade: -1
                    });
                }
            });
    }

    update() {
        if (this.paused) return;
        if (Q3.assigningLine) return;
        super.update(...arguments);
    }

    getObjectAction(objectUuid) {
        return this.objectsActions[objectUuid] = this.objectsActions[objectUuid] || {
            activeAction: null,
            status: Status.NOT_PLAYING,
            actions: {},
        };
    }

    _uncacheIfPreset(action) {
        const animData = SlideManager.currentSlide.animation[action._localRoot.uuid];

        if (!animData || !action._clip.isPreset && action.enabled) return;

        this.uncacheAction(action._clip, action._localRoot);

        const index = this._actions.indexOf(action);

        if (index >= 0) this._actions.splice(index, 1);

        action.enabled = false;
    }

    _actionFinished(e) {
        const { action } = e;
        const rootObject = action.getRoot();
        const objectActions = this.getObjectAction(rootObject.uuid);

        if (!objectActions) console.warn("Finished action for object " + rootObject.uuid + " that doesn't have an action assigned");
        else {
            e.action.time = e.action.timeScale >= 0 ? e.action.getClip().duration : 0;
            this.update(0);
            // The animation needs to stop altogether to keep from interfering
            // with other animations but the objects should stay where they
            // were at the end of the animation so we restore the coordinates
            // after we stop the animation
            let allParams = [];
            rootObject.traverse(object => {
                const params = {
                    position: object.position.clone(),
                    quaternion: object.quaternion.clone(),
                    scale: object.scale.clone(),
                };

                if (object.morphTargetInfluences) {
                    params.morphTargetInfluences = object.morphTargetInfluences.slice();
                }

                allParams.push({ object, params });
            });

            // e.action.paused = true;
            e.action.stop();

            this._clearActionStopTimer(e.action);
            this._uncacheIfPreset(e.action);
            this.update(0);

            for (let { object, params } of allParams) {
                object.position.copy(params.position);
                object.quaternion.copy(params.quaternion);
                object.scale.copy(params.scale);

                if (params.morphTargetInfluences) {
                    for (const [i, weight] of params.morphTargetInfluences.entries()) {
                        object.morphTargetInfluences[i] = weight;
                    }
                }
            }

            objectActions.activeAction = null;
            objectActions.status = Status.NOT_PLAYING;

            this.actionStatusChanged.fire({ objectUuid: rootObject.uuid, status: Status.NOT_PLAYING });
        }
    }

    getObjectAnimationStatus(objectUuid) { return this.getObjectAction(objectUuid).status; }

    stopObjectCurrentAnimation(objectUuid, fade = 1) {
        const objectActions = this.getObjectAction(objectUuid);

        if (!!objectActions.activeAction) {
            let action = objectActions.activeAction;

            if (fade === -1) {
                action.stop();
                this._clearActionStopTimer(action);
                this._uncacheIfPreset(action);
            } else {
                if (fade === 0) action.timeScale = 1e10;
                else action.timeScale = (action.getClip().duration - action.time) / fade;
                action.loop = THREE.LoopOnce;
                this._setActionStopTimer(action, fade);
            }

            objectActions.activeAction = null;
            objectActions.status = Status.NOT_PLAYING;
            this.actionStatusChanged.fire({ objectUuid, status: Status.NOT_PLAYING });
        }
    }

    _setActionStopTimer(action, time) {
        if (this._actionStopTimers.has(action))
            this._clearActionStopTimer(action);

        const rootObject = action.getRoot();
        const objectActions = this.getObjectAction(rootObject.uuid);
        const timeout = setTimeout(() => {
            action.stop();
            this._actionStopTimers.delete(action);
            this._uncacheIfPreset(action);

            objectActions.activeAction = null;
            objectActions.status = Status.NOT_PLAYING;
        }, time * 1000);

        this._actionStopTimers.set(action, timeout);
    }

    _clearActionStopTimer(action) {
        if (!this._actionStopTimers.has(action)) return;

        let timeout = this._actionStopTimers.get(action);

        clearTimeout(timeout);

        this._actionStopTimers.delete(action);
    }

    pauseObjectCurrentAnimation(objectUuid) {
        const objectActions = this.getObjectAction(objectUuid);

        if (objectActions.activeAction) {
            objectActions.activeAction.paused = true;
            objectActions.status = Status.PAUSED;
            this.actionStatusChanged.fire({ objectUuid, status: Status.PAUSED });
        }
    }

    playObjectAnimation({ objectUuid, animationUuid, loopType = THREE.LoopRepeat, timescale = 1, fade = 1, repetitions = Infinity, timeRange, resume = false , enableDelay = true}) {
        const delaySettings = SystemDefaults.getAnimationDelaySettings().animationDelay;
        let animationDelay = Q3.data.slides[Q3.SlideManager.activeSlide].animation[objectUuid]?.animationDelay?.delay;
        
        if (!animationDelay || !enableDelay || animationDelay < delaySettings.min || animationDelay > delaySettings.max) animationDelay = 0;

        if (!!objectUuid && animationUuid) {
            const objectActions = this.getObjectAction(objectUuid);

            let fullClip = AssetLibrary.animations[animationUuid];
            let { min: startTime, max: endTime } = getValidTimeRange(fullClip, timeRange);
            let animKey = animationUuid;
            let clip;

            if (Math.abs(startTime) > 0.01 || Math.abs(endTime - fullClip.duration) > 0.01) {
                animKey = [animationUuid, startTime.toFixed(2), endTime.toFixed(2)].join(' ');

                clip = this._partialAnimClips[animKey];
                if (!clip) {
                    clip = subclip(fullClip, startTime, endTime);
                    this._partialAnimClips[animKey] = clip;
                }
            } else clip = fullClip;

            let action = objectActions.actions[animKey];

            if (this.animationDelayer.has(objectUuid)) {
                clearTimeout(this.animationDelayer.get(objectUuid));
            }

            const animationDelayer = setTimeout(() => {
                this.animationDelayer.delete(objectUuid);

                if (resume && objectActions.activeAction === action && objectActions.status === Status.PAUSED) {
                    objectActions.activeAction.paused = false;
                    objectActions.status = Status.PLAYING;
                    this.actionStatusChanged.fire({ objectUuid: objectUuid, status: Status.PLAYING });

                } else {
                    //stop current one if any
                    this.stopObjectCurrentAnimation(objectUuid, fade);

                    if (clip) {
                        if (!action || action !== objectActions.activeAction) {
                            const object = Q3.assets.objects[objectUuid] || Q3.assets.lights[objectUuid];

                            //lazy action init
                            if (!action) {
                                action = this.clipAction(clip, object);
                                action.clampWhenFinished = true;
                                objectActions.actions[animKey] = action;
                            }

                            generateAnimatedBoundingBox(object);

                            //play new animation
                            objectActions.activeAction = action;
                            objectActions.activeAction.fadeIn(fade);
                            objectActions.activeAction.reset();
                            objectActions.activeAction.play();
                            objectActions.status = Status.PLAYING;
                            this._clearActionStopTimer(action);
                            this.actionStatusChanged.fire({ objectUuid: objectUuid, status: Status.PLAYING });
                        }

                        if (loopType !== objectActions.activeAction.loop || repetitions !== objectActions.activeAction.repetitions) {
                            objectActions.activeAction.setLoop(loopType, repetitions);
                            objectActions.activeAction.reset();
                            objectActions.activeAction.play();
                            this._clearActionStopTimer(objectActions.activeAction);
                        }

                        objectActions.activeAction.setEffectiveTimeScale(timescale);
                    }
                }
            }, animationDelay * 1000);
            this.animationDelayer.set(objectUuid, animationDelayer);
        }
    }

    setObjectAnimationFrame({ objectUuid, animationUuid, frame/* 0 to 1*/, timeRange }) {
        if (!objectUuid) return;

        const objectActions = this.getObjectAction(objectUuid);
        //stop current one if any
        this.stopObjectCurrentAnimation(objectUuid, 0);

        const clip = AssetLibrary.animations[animationUuid];

        if (!clip) return;
        if (!(!objectActions.actions[animationUuid] || objectActions.actions[animationUuid] !== objectActions.activeAction)) return;

        //lazy action init
        if (!objectActions.actions[animationUuid]) {
            const object = Q3.assets.objects[objectUuid] || Q3.assets.lights[objectUuid];
            const action = this.clipAction(clip, object);
            objectActions.actions[animationUuid] = action;
        }

        objectActions.activeAction = objectActions.actions[animationUuid];
        objectActions.status = Status.PAUSED;

        this.actionStatusChanged.fire({ objectUuid: objectUuid, status: Status.PAUSED });

        objectActions.activeAction.stop();

        this._clearActionStopTimer(objectActions.activeAction);
        this.repetition = 0;
        this.clock = new THREE.Clock();

        let { min: startTime, max: endTime } = getValidTimeRange(clip, timeRange);
        let frameFull = startTime + (endTime - startTime) * frame;

        objectActions.activeAction.time = frameFull;
        objectActions.activeAction.enabled = true;
        objectActions.activeAction.play();

        this._clearActionStopTimer(objectActions.activeAction);
        this.update(0);

        objectActions.activeAction.paused = true;
    }

    async loadAndPlay({ objectUuid, animationUuid, loopType = THREE.LoopRepeat, timescale = 1 }) {
        if (!Q3.assets.animations[animationUuid]) {
            const data = Q3.data.assets.animations[animationUuid];

            await loadAnimation({
                loaders: AssetDeserializer.loaders,
                assets: AssetLibrary,
                item: { data, uuid: animationUuid },
                data: Q3.data,
                mixer: this,
                slide: SlideManager.activeSlide,
                animationsWaiting: this._waitingObjectAnimation
            });
        }

        this.playObjectAnimation({
            objectUuid,
            animationUuid,
            loopType,
            timescale
        });
    }

    createAndPlayPresetAnimation(presetParams) {
        const delaySettings = SystemDefaults.getAnimationDelaySettings().animationDelay;
        const { type, objectUuid, loopType, timescale, fade = -1, repetitions = Infinity, enableDelay = true } = presetParams;
        const framerate = 30;

        let animationDelay = Q3.data.slides[Q3.SlideManager.activeSlide].animation[objectUuid]?.animationDelay?.delay;
        
        if (!animationDelay || !enableDelay || animationDelay < delaySettings.min || animationDelay > delaySettings.max) animationDelay = 0;

        this.stopObjectCurrentAnimation(objectUuid, fade);

        let builder;

        switch (type) {
            case "anim-spin": builder = presets.createSpinAnim; break;
            case "anim-pop-in": builder = presets.createPopInAnim; break;
            case "anim-slide-in": builder = presets.createSlideInAnim; break;
            case "anim-assemble": builder = presets.createAssemble; break;
            default: throw new Error(`Unsupported animation type: ${type}`);
        }

        const object = Q3.assets.objects[objectUuid] || Q3.assets.lights[objectUuid];
        const defaults = SystemDefaults.getPresetAnimations();
        const clip = builder(Object.assign(defaults, { framerate, object }, presetParams));

        clip.isPreset = true;

        const objectActions = this.getObjectAction(objectUuid);
        const animKey = clip.name;

        //stop current one if any

        /**
         * @type {THREE.AnimationAction}
         */
        let action = null;

        //lazy action init
        if (!action) {
            action = this.clipAction(clip, object);
            action.clampWhenFinished = true;
            objectActions.actions[animKey] = action;
        }

        if (this.animationDelayer.has(objectUuid)) {
            clearTimeout(this.animationDelayer.get(objectUuid));
        }

        //play new animation
        const animationDelayer = setTimeout(() => {
            this.animationDelayer.delete(objectUuid);

            objectActions.activeAction = action;

            action
                .setLoop(loopType, repetitions)
                .setEffectiveTimeScale(timescale)
                .fadeIn(fade)
                .reset()
                .setTime(loopType === THREE.LoopOnce && timescale < 0 ? objectActions.activeAction._clip.duration : null)
                .play();

            objectActions.status = Status.PLAYING;

            this._clearActionStopTimer(action);

            this.actionStatusChanged.fire({ objectUuid: objectUuid, status: Status.PLAYING });

            return action;

        }, animationDelay * 1000);
        this.animationDelayer.set(objectUuid, animationDelayer);
    }
}

const TimeRangeEmpty = {};

function getValidTimeRange(clip, timeRange) {
    let { min, max } = timeRange || TimeRangeEmpty;
    if (min === undefined) min = 0;
    if (max === undefined) max = clip.duration;
    return { min, max };
}

function getObjectOriginalName(object) { return "originalName" in object ? object.originalName : object.name; }

function findNode(root, nodeName) {

    if (
        !nodeName
        || nodeName === ""
        || nodeName === "root"
        || nodeName === "."
        || nodeName === -1
        || nodeName === root.name
        || nodeName === root.uuid) return root;

    // search into skeleton bones.
    if (root.skeleton) {
        for (let i = 0, il = root.skeleton.bones.length; i < il; i++) {
            const bone = root.skeleton.bones[i];
            if (getObjectOriginalName(bone) === nodeName) return bone;
        }
    }

    // search into node subtree.
    if (root.children) {
        const searchNodeSubtree = function (children) {
            for (let i = 0; i < children.length; i++) {
                const childNode = children[i];

                if (
                    getObjectOriginalName(childNode) === nodeName
                    || childNode.uuid === nodeName) return childNode;

                const result = searchNodeSubtree(childNode.children);

                if (result) return result;
            }
            return null;
        };

        const subTreeNode = searchNodeSubtree(root.children);

        if (subTreeNode) return subTreeNode;
    }

    return null;
};

export default Q3AnimationMixer;
export { Q3AnimationMixer };